import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ClusteredMap from 'modules/maps/mapboxGL/clusteredMap';
import useI18n from 'hooks/I18n/useI18n';

export const AssembliesMap = React.forwardRef(
    (
        {
            currentAssembly,
            assemblies,
            pickups,
            onMarkerSelect,
            onMapClick,
            isSelectedMarkerLayerVisible,
            panToCoordinates,
            onMapMoveStart,
            onMapMoveEnd,
            isVisible = true,
            onMapLoaded,
        },
        map
    ) => {
        const { i18n } = useI18n();
        const [isLoaded, setIsLoaded] = useState(false);
        const mapContainer = useRef(null);
        const isEventHandlersAttached = useRef(false);

        useEffect(() => {
            if (!map.current && mapContainer.current) {
                map.current = new ClusteredMap(mapContainer, i18n, () => setIsLoaded(true));
                const realMap = map.current.getMapInstance();
                onMapLoaded(realMap.getCenter().lng, realMap.getCenter().lat);
                if (onMapMoveStart) {
                    realMap.on('movestart', onMapMoveStart);
                }
                if (onMapMoveEnd) {
                    realMap.on('moveend', () => {
                        const { lng, lat } = realMap.getCenter();
                        const zoom = realMap.getZoom();
                        onMapMoveEnd({ lon: lng, lat, zoom });
                    });
                }
            }

            return () => {
                if (!map.current) return;

                map.current = null;
                isEventHandlersAttached.current = false;
            };
        }, [i18n, onMapMoveStart, onMapMoveEnd, map, onMapLoaded]);

        useEffect(() => {
            if (assemblies.length === 0 && pickups.length === 0) {
                return;
            }

            map.current.refreshMainSourceData(assemblies, pickups);

            if (!isEventHandlersAttached.current) {
                map.current.attachEventHandlers(onMarkerSelect, onMapClick);
                isEventHandlersAttached.current = true;
            }
        }, [assemblies, pickups, onMarkerSelect, onMapClick, map]);

        useEffect(() => {
            if (!isSelectedMarkerLayerVisible) {
                map.current.hideActiveMarker();
            }
        }, [isSelectedMarkerLayerVisible, map]);

        useEffect(() => {
            if (panToCoordinates === null) {
                return;
            }

            map.current.panTo(panToCoordinates, map.current.getMapInstance().getZoom());
        }, [panToCoordinates, map]);

        useEffect(() => {
            if (!map.current || !isLoaded) return;
            let longitude =
                currentAssembly?.properties?.place?.address?.coordinates?.longitude ||
                map.current.map.getCenter().lng;
            let latitude =
                currentAssembly?.properties?.place?.address?.coordinates?.latitude ||
                map.current.map.getCenter().lat;
            if (currentAssembly?.type === 'pickup') {
                longitude = currentAssembly.properties?.meetingPoint?.coordinates.longitude;
                latitude = currentAssembly.properties?.meetingPoint?.coordinates.latitude;
            }

            if (longitude === undefined || latitude === undefined || !currentAssembly) return;

            const realMap = map.current.getMapInstance();

            realMap.easeTo({
                center: [longitude, latitude],
                offset: [12, 150],
                duration: 1000,
            });
            currentAssembly && map.current.selectHiveById(currentAssembly.properties.id);
        }, [currentAssembly, isLoaded, map]);

        useEffect(() => {
            if (isVisible && map.current) {
                map.current.getMapInstance().resize();
            }
        }, [isVisible, map]);

        return (
            <div ref={mapContainer} className={classNames('pi-map', { visible: isVisible })}></div>
        );
    }
);

AssembliesMap.propTypes = {
    assemblies: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    pickups: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    onMarkerSelect: PropTypes.func.isRequired,
    onMapClick: PropTypes.func.isRequired,
    isSelectedMarkerLayerVisible: PropTypes.bool.isRequired,
    panToCoordinates: PropTypes.array,
    currentAssembly: PropTypes.shape({ properties: PropTypes.shape({ id: PropTypes.string }) }),
    onMapMoveStart: PropTypes.func,
    onMapMoveEnd: PropTypes.func,
    isVisible: PropTypes.bool,
    onMapLoaded: PropTypes.func.isRequired,
};

export default React.memo(AssembliesMap);
